import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { Button } from '#app/components/ui/button.tsx'

export const meta: MetaFunction = () => {
	return [{ title: 'AI Executive Roles | Axiom Documentation' }]
}

export default function AiRolesRoute() {
	return (
		<main className="container mx-auto py-16 px-4">
			<div className="max-w-4xl mx-auto">
				<div className="mb-8">
					<Link to="/docs" className="text-muted-foreground hover:text-primary">
						← Back to Documentation
					</Link>
				</div>

				<h1 className="text-4xl font-bold mb-12">Executive Roles & Capabilities</h1>

				<div className="prose prose-lg dark:prose-invert max-w-none space-y-12">
					<section>
						<h2 className="text-3xl font-semibold mb-6">Strategic Leadership Team</h2>
						
						<div className="grid gap-6 md:grid-cols-2">
							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Executive Officer (CEO)</h3>
								<p className="text-muted-foreground">
									Provides high-level strategic direction and organizational leadership.
									Helps align initiatives with overall business objectives and vision.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Operating Officer (COO)</h3>
								<p className="text-muted-foreground">
									Focuses on operational excellence and process optimization.
									Helps streamline business operations and improve efficiency.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Financial Officer (CFO)</h3>
								<p className="text-muted-foreground">
									Provides financial analysis, budgeting guidance, and investment
									strategy. Helps optimize resource allocation and financial
									planning.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Marketing Officer (CMO)</h3>
								<p className="text-muted-foreground">
									Offers expertise in marketing strategy, brand development, and
									customer acquisition. Helps create effective marketing campaigns.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Technology Officer (CTO)</h3>
								<p className="text-muted-foreground">
									Provides guidance on technology strategy, digital transformation,
									and technical infrastructure. Helps evaluate technology solutions.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Product Officer (CPO)</h3>
								<p className="text-muted-foreground">
									Leads product strategy, development, and innovation initiatives.
									Helps align product roadmap with market needs and business goals.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Revenue Officer (CRO)</h3>
								<p className="text-muted-foreground">
									Focuses on revenue growth strategies and sales optimization.
									Helps develop and execute revenue generation initiatives.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Customer Officer (CCO)</h3>
								<p className="text-muted-foreground">
									Champions customer experience and satisfaction initiatives.
									Helps optimize customer journey and build lasting relationships.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Human Resources Officer (CHRO)</h3>
								<p className="text-muted-foreground">
									Provides guidance on organizational development and talent strategy.
									Helps optimize team structure and culture initiatives.
								</p>
							</div>

							<div className="border rounded-lg p-6 bg-background hover:bg-muted/50 transition-colors">
								<h3 className="text-xl font-semibold mb-3">Chief Legal Officer (CLO)</h3>
								<p className="text-muted-foreground">
									Offers legal and compliance expertise for business operations.
									Helps navigate regulatory requirements and minimize legal risks.
								</p>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-3xl font-semibold mb-6">Team Interaction</h2>
						
						<div className="bg-muted/50 rounded-lg p-6">
							<p className="mb-3">
								When you ask the team a question or make a comment, specific executives will respond based on the content
								and context of your message. The Axiom automatically routes your inquiry to the most
								relevant team members.
							</p>
							<p>
								To directly engage with specific executives, simply @mention them in your message
								(e.g., @ceo or @cto), and they will respond to your message directly.
							</p>
						</div>
					</section>

					<div className="pt-8">
						<Button asChild>
							<Link to="/docs/collaboration-guide">
								Next: Collaboration Guide →
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</main>
	)
} 